import isUndefined from 'lodash-es/isUndefined'
import React, { cloneElement, useState } from 'react'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import { CloseIcon, DragIcon } from '@fourthwall/icons'
import { Icon } from '../Icon'
import { HELPER_CLASS } from './consts'
import * as S from './styled'
import type {
  ContainerProps,
  ElementProps,
  ItemProps,
  SortableBlocksProps,
  ToolbarProps,
} from './types'

const DragHandle = SortableHandle(() => (
  <S.DragHandle title="Drag">
    <Icon component={DragIcon} height={14} />
  </S.DragHandle>
))

export const Toolbar = ({ onRemove }: ToolbarProps) => {
  return (
    <S.ToolbarContainer>
      <DragHandle />
      <S.Handle title="Remove" onClick={onRemove} data-testid="SortableBlocks.RemoveHandle">
        <Icon component={CloseIcon} height={14} />
      </S.Handle>
    </S.ToolbarContainer>
  )
}

const SortableBlocksElement = SortableElement<ElementProps>(
  ({ isDragged, children, onRemove }: ElementProps) => (
    <S.Element isDragged={isDragged}>
      {children}
      <Toolbar onRemove={onRemove} />
    </S.Element>
  ),
)

const SortableBlockItem = ({ index, isDragged, children, onRemove }: ItemProps) => {
  if (isUndefined(index) || isUndefined(isDragged)) return null

  const handleRemove = () => {
    if (onRemove) {
      onRemove(index)
    }
  }

  return (
    <S.Item data-testid="SortableBlocks.Item">
      <S.Placeholder />
      <SortableBlocksElement index={index} isDragged={isDragged} onRemove={handleRemove}>
        {children}
      </SortableBlocksElement>
    </S.Item>
  )
}

const SortableBlocksContainer = SortableContainer<ContainerProps>(
  ({ isDragged, children, onRemove }: ContainerProps) => {
    return (
      <S.Container>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return cloneElement<any>(child, { index, isDragged, onRemove })
          }
        })}
      </S.Container>
    )
  },
)

export const SortableBlocks = ({ children, onSortChange, onRemove }: SortableBlocksProps) => {
  const [isDragged, setIsDragged] = useState(false)

  const handleSortStart = () => {
    setIsDragged(true)
  }

  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setIsDragged(false)

    if (onSortChange) {
      onSortChange(oldIndex, newIndex)
    }
  }

  return (
    <SortableBlocksContainer
      isDragged={isDragged}
      helperClass={HELPER_CLASS}
      useDragHandle
      distance={1}
      axis="y"
      onSortStart={handleSortStart}
      onSortEnd={handleSortEnd}
      onRemove={onRemove}
    >
      {children}
    </SortableBlocksContainer>
  )
}

SortableBlocks.Item = SortableBlockItem
